// ============================================================
// THE AMORPHOUS MASS, MOVING (plan body-map S2; the goo study's body(),
// ruled 2026-10-09). Where every drop of the mass is at a moment, and what
// its conditions add round it - as data, for the plate's clock to place.
//
//   its idle: the core drifts, the satellites swim, now and then one strays
//   and pinches off (its nature, not a condition: it moves even at rest)
//   Fatigue: it sags lower by level and heaves; Unconscious breathes slow
//   On Fire: its top boils, drops evaporate off it
//   Snared: two bands cinch it, the goo bulging between, tightening as it
//   strains; Stunned: it quivers; Dying: it sheds drops and shrinks;
//   Dead: it settles into a still puddle
//   round it: Blood Loss's drops falling into the ring, Immobilised's stasis
//   field, Cannot Speak's pulse dying before the surface, the bubbles (dark
//   when Blinded)
//
// `idle` is the mass's own clock, which runs at its PACE (slower when tired,
// unconscious or held; stopped when paralysed) and so must be accumulated
// (idle += dt x pace) - scaling time by the pace would teleport it when the
// pace changes. `t` is real time. Dying and Dead are EASED weights 0..1.
//
// Pure. Zero React dependency. Frame: the study's, ground at y 650.
// ============================================================

import type { Drop } from './goo';
import { hash01 } from './hash';

export interface GooCondition {
  fatigue: number;
  unconscious: boolean;
  paralysed: boolean;
  immob: boolean;
  fire: boolean;
  snared: boolean;
  stun: boolean;
  /** Eased weights, 0..1. */
  dying: number;
  dead: number;
}

export interface GooBody {
  cx: number;
  cy: number;
  cr: number;
  cores: Drop[];
  sats: Drop[];
  /** Snared's two bands: their height, their half-width, how hard the goo strains against them. */
  bands: { y: number; rx: number; strain: number }[] | null;
}

/** Nothing happening to it: the mass at rest, and the base every condition is read against. */
export const CALM: GooCondition = {
  fatigue: 0,
  unconscious: false,
  paralysed: false,
  immob: false,
  fire: false,
  snared: false,
  stun: false,
  dying: 0,
  dead: 0,
};

const SATELLITES = 9;
/** The ground the mass stands on: the projector's ring, where falling drops land (the study's frame). */
export const FOOT = 650;

/** A bell round 0 of the given width - how near a drop is to a band. */
export const bell = (x: number, w: number) => Math.exp(-(x * x) / (2 * w * w));

/** The mass's own pace: slower when tired, unconscious or held; stopped when paralysed. */
export function gooPace(c: Pick<GooCondition, 'paralysed' | 'unconscious' | 'immob' | 'fatigue'>): number {
  if (c.paralysed) return 0;
  return (c.unconscious ? 0.25 : 1) * (c.immob ? 0.15 : 1) * (1 - 0.15 * c.fatigue);
}

/** Where the core is, and how big: drifting, sagging with fatigue, heaving, quivering when stunned, flat when dead. */
function core(ti: number, t: number, c: GooCondition) {
  const shrink = 1 - 0.3 * c.dying;
  const quiver = c.stun ? 6 : 0;
  const cx = 300 + 14 * Math.sin(0.4 * ti) + quiver * Math.sin(40 * t);
  // The study's drift, its phase turned so moment 0 is the settled mass the plate drew still (P7).
  const cy = 380 + 20 * Math.sin(0.3 * ti) + 200 * c.dead + 16 * c.fatigue;
  let cr = 120 * shrink * (1 + 0.035 * c.fatigue * Math.sin(2.2 * t));
  if (c.unconscious) cr *= 1 + 0.03 * Math.sin(0.9 * t);
  return { cx, cy, cr, shrink, quiver };
}

/** What every satellite reads: the clocks, the condition, the core, and whether to leave out the stray. */
interface SatContext {
  ti: number;
  t: number;
  c: GooCondition;
  at: ReturnType<typeof core>;
  settled: boolean;
}

/** How far a satellite swims from the core and how big it is, before Dying sheds it and Dead flattens it. */
function swim(i: number, base: number, { ti, t, c, at, settled }: SatContext) {
  const stray = settled ? 0 : 80 * Math.pow(Math.max(0, Math.sin(0.21 * ti + i)), 6);
  const d = 95 + 45 * Math.sin(0.5 * ti + 1.7 * i) * (1 - 0.15 * c.fatigue) + stray;
  const r = (44 + 16 * Math.sin(0.7 * ti + i)) * at.shrink;
  const boils = c.fire && Math.sin(base) < -0.2;
  return { d, r: boils ? r * (1 + 0.18 * Math.sin(14 * t + i * 2)) : r };
}

/** Which way satellite `i` lies from the core at the mass's own clock `ti`: evenly round it, swaying. */
export const satAngle = (i: number, ti: number) => (i / SATELLITES) * Math.PI * 2 + 0.3 * Math.sin(0.2 * ti + i);

/** One satellite drop: swimming round the core, now and then straying; boiling on top when on fire; shed when dying. */
function satellite(i: number, ctx: SatContext): Drop {
  const { ti, t, c, at } = ctx;
  const base = satAngle(i, ti);
  let { d, r } = swim(i, base, ctx);
  const shed = c.dying * ((ti * 0.25 + i / 9) % 1);
  d = (d + 160 * shed) * (1 + 0.5 * c.dead);
  r *= 1 - shed;
  const x = at.cx + d * Math.cos(base) + at.quiver * Math.sin(37 * t + i);
  const y = at.cy + d * Math.sin(base) * (1.15 - 0.95 * c.dead);
  return [x, y, Math.max(0, r * (1 - 0.45 * c.dead))];
}

/** Snared: two bands round the mass pinch it in; the goo bulges between them, tightening as it strains. */
function cinch(body: GooBody, t: number): GooBody {
  const { cx, cy, cr } = body;
  const strain = Math.max(0, Math.sin(2.6 * t));
  const pinch = 0.2 + 0.12 * strain;
  const at = [cy - 0.22 * cr, cy + 0.5 * cr];
  const squeeze = ([x, y, r]: Drop): Drop => {
    const f = at.reduce((k, by) => k - pinch * bell(y - by, 0.28 * cr), 1);
    return [cx + (x - cx) * f, y, r * (0.7 + 0.3 * f)];
  };
  return {
    ...body,
    cores: body.cores.map(squeeze),
    sats: body.sats.map(squeeze),
    bands: at.map((y) => ({ y, rx: cr * 1.08 * (1 - pinch * 0.85), strain })),
  };
}

/**
 * The mass at a moment: its cores and satellites, and Snared's bands. `settled` leaves out the stray drop - the
 * still form, which must read as settled, not as a drop hanging loose (P7).
 */
export function gooBody(ti: number, t: number, c: GooCondition, settled = false): GooBody {
  const at = core(ti, t, c);
  const flatR = 1 - 0.35 * c.dead;
  const body: GooBody = {
    cx: at.cx,
    cy: at.cy,
    cr: at.cr,
    cores: [
      [at.cx, at.cy, at.cr * flatR],
      [at.cx, at.cy, at.cr * 0.95 * flatR],
    ],
    sats: Array.from({ length: SATELLITES }, (_, i) => satellite(i, { ti, t, c, at, settled })),
    bands: null,
  };
  return c.snared ? cinch(body, t) : body;
}

/** Each falling drop's own rhythm and place: a fixed hash, never Math.random (gotcha 5). */
const hash = hash01;

export interface LeakDrop {
  x: number;
  y: number;
  r: number;
  /** Stretched as it falls. */
  ry: number;
  opacity: number;
  /** Its splash in the ring as it lands: radius and opacity (0 while it falls). */
  splash: number;
  splashOpacity: number;
}

/** Where one falling drop is in its cycle: gathering on the underside (f 0), falling (f to 1), splashing (s 0..1). */
function fallOf(ph: number, wait: number) {
  if (ph < wait) return { f: 0, gather: ph / wait, s: -1 };
  const f = Math.min(1, (ph - wait) / ((1 - wait) * 0.85));
  return { f, gather: 1, s: f >= 1 ? ((ph - wait) / (1 - wait) - 0.85) / 0.15 : -1 };
}

/** Where a drop is and how it looks, from where it is in its fall. */
function dropAt(fall: ReturnType<typeof fallOf>, y0: number, r: number) {
  const { f, gather, s } = fall;
  const falling = f > 0;
  return {
    y: falling ? y0 + (FOOT - y0) * f * f : y0 + 4 * gather,
    ry: r * (1 + 0.8 * f),
    opacity: falling ? Number(f < 1) : gather,
    splash: 4 + 22 * Math.max(0, s),
    splashOpacity: s >= 0 ? 1 - s : 0,
  };
}

/** One drop's fall: its own rhythm, and a new place, size and pause each time it falls. */
function leakDrop(k: number, t: number, body: GooBody, severe: boolean): LeakDrop {
  const cyc = t * (severe ? 0.6 : 0.32) * (0.7 + 0.6 * hash(k, 0.5)) + hash(k, 1.5);
  const round = Math.floor(cyc);
  const r = 4.5 + 3.5 * hash(k, round + 5.7);
  const fall = fallOf(cyc - round, 0.15 + 0.4 * hash(k, round + 7.1));
  const x = body.cx + (hash(k, round + 3.3) - 0.5) * body.cr * 1.3;
  return { x, r, ...dropAt(fall, body.cy + body.cr * 0.82, r) };
}

/**
 * Blood Loss: drops gather on the underside and fall into the ring, each splashing. Each keeps its own rhythm and
 * takes a new place and pause each fall, so they come irregularly; severe: twice as many, and faster.
 */
export function leakDrops(t: number, body: GooBody, level: number): LeakDrop[] {
  const n = level === 2 ? 8 : level ? 4 : 0;
  return Array.from({ length: n }, (_, k) => leakDrop(k, t, body, level === 2));
}

/** On Fire: ten drops evaporating off the top, rising and fading; hot while young. */
export function fireWisps(t: number, body: GooBody) {
  return Array.from({ length: 10 }, (_, k) => {
    const ph = (t * 0.45 + k / 10) % 1;
    return {
      x: body.cx + (k - 4.5) * 18 + 10 * Math.sin(4 * ph + k),
      y: body.cy - body.cr * 0.9 - 130 * ph,
      r: 2 + 3 * ph,
      opacity: (1 - ph) * 0.8,
      hot: ph < 0.3,
    };
  });
}

/** Immobilised: a stasis field - a cylinder of cold light on the projector ring, scanned by three slow rings. */
export function stasisField(t: number, body: GooBody) {
  const R = body.cr * 1.45;
  const top = body.cy - body.cr * 1.55;
  const scans = [0, 1, 2].map((k) => {
    const ph = (t * 0.12 + k / 3) % 1;
    return {
      y: FOOT - (FOOT - top) * (0.5 - 0.5 * Math.cos(2 * Math.PI * ph)),
      opacity: 0.25 + 0.35 * Math.sin(Math.PI * ph),
    };
  });
  return { cx: body.cx, R, top, foot: FOOT, scans };
}

/** Cannot Speak: a pulse leaves the core and dies before it reaches the surface. */
export function voicePulse(t: number, body: GooBody) {
  const vp = (t * 0.6) % 1;
  return { r: body.cr * 0.7 * vp, opacity: Math.sin(Math.PI * vp) * (1 - vp) };
}

/** The bubbles' depths: the deeper, the smaller, slower and fainter. */
const BUBBLE_DEPTHS = [0.15, 0.35, 0.55, 0.75, 0.95, 0.25, 0.65];

/** Bubbles rising through the core as lit spheres; Blinded: its eyes go dark (the bubbles all but out). */
export function gooBubbles(t: number, body: GooBody, c: Pick<GooCondition, 'dying' | 'dead'>, blind: boolean) {
  const shrink = 1 - 0.3 * c.dying;
  return BUBBLE_DEPTHS.map((z, i) => {
    const rise = (t * (0.04 + 0.05 * z) + i * 0.37) % 1;
    return {
      x: body.cx + (30 + 30 * z) * shrink * Math.sin(0.3 * t * (0.6 + z) + i * 1.9),
      y: body.cy + (1 - c.dead) * (85 - 170 * rise) * shrink - 20 + c.dead * 10,
      r: (5 + 11 * z) * shrink * (1 - 0.5 * c.dead),
      opacity: Math.sin(Math.PI * rise) * (0.35 + 0.65 * z) * (blind ? 0.15 : 1),
    };
  });
}
